<script>
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import AvailabilityPicker from '../components/AvailabilityPicker.vue';
import { FIELD_CLASS } from '../constants';
import { reportInvalidForm } from '../formFeedback';
import { bookingErrorMessage } from '../../../../api/realEstate/errors';

// Reschedule always follows the project's *current* booking mode, not
// whatever mode the appointment was originally booked under (docs/
// real-estate/be/10-api-reference.md §A13 PUT .../reschedule) — a walk-in
// row at a project that's since switched to Agent must now pick an agent,
// and vice versa. That's why this fetches the project detail fresh rather
// than trusting `appointment.booking_mode`.
export default {
  name: 'RescheduleModal',
  components: { AvailabilityPicker },
  props: {
    appointment: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      fieldClass: FIELD_CLASS,
      durationMinutes: this.appointment.duration_minutes,
      reason: '',
      availability: null,
      isSubmitting: false,
      submitted: false,
    };
  },
  computed: {
    ...mapGetters('realEstate', { getProject: 'getProject' }),
    project() {
      return this.getProject(this.appointment.property.property_id);
    },
    bookingMode() {
      return this.project ? this.project.booking_mode : 'walk_in';
    },
    isKeyHandover() {
      return this.appointment.appointment_type === 'key_handover';
    },
    fieldErrors() {
      return {
        availability: !this.availability,
        duration: this.isKeyHandover && !this.durationMinutes,
      };
    },
    isFormValid() {
      return !Object.values(this.fieldErrors).some(Boolean);
    },
  },
  mounted() {
    this.fetchProject(this.appointment.property.property_id);
  },
  methods: {
    ...mapActions('realEstate', ['fetchProject']),
    ...mapActions('realEstateAppointments', ['rescheduleAppointment']),
    handleDurationChange(event) {
      this.durationMinutes = Number(event.target.value) || null;
    },
    handleAvailabilityChange(value) {
      this.availability = value;
    },
    async handleSubmit() {
      this.submitted = true;
      if (!this.isFormValid) {
        reportInvalidForm(this);
        return;
      }
      this.isSubmitting = true;
      try {
        const fields = {
          date: this.availability.date,
          start_time: this.availability.start_time,
          reason: this.reason || null,
        };
        if (this.availability.agent_id) {
          fields.agent_id = this.availability.agent_id;
        } else if (this.availability.auto_assign_agent) {
          fields.auto_assign_agent = true;
        }
        if (this.isKeyHandover) fields.duration_minutes = this.durationMinutes;
        await this.rescheduleAppointment({
          id: this.appointment.appointment_id,
          fields,
        });
        useAlert(this.$t('REAL_ESTATE.APPOINTMENTS.SUCCESS_RESCHEDULED'));
        this.$emit('close');
      } catch (err) {
        useAlert(
          bookingErrorMessage(this, err, {
            isWalkIn: this.bookingMode === 'walk_in' && !this.isKeyHandover,
            fallbackKey: 'REAL_ESTATE.APPOINTMENTS.ERROR_RESCHEDULE',
          })
        );
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <div
    class="fixed inset-0 flex items-center justify-center bg-slate-800/80 z-50"
  >
    <div
      class="bg-white dark:bg-slate-900 rounded-lg p-6 shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto"
    >
      <div class="flex items-center mb-4 justify-between">
        <h1 class="text-xl font-bold text-slate-900 dark:text-slate-100">
          {{ $t('REAL_ESTATE.APPOINTMENTS.MODAL_RESCHEDULE_TITLE') }}
        </h1>
        <woot-button
          variant="clear"
          color-scheme="alert"
          icon="dismiss"
          @click="$emit('close')"
        />
      </div>

      <form
        v-if="project"
        class="space-y-4"
        novalidate
        @submit.prevent="handleSubmit"
      >
        <div v-if="isKeyHandover">
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.APPOINTMENTS.FIELD_DURATION') }}
            <span class="text-red-500">*</span>
          </label>
          <input
            type="number"
            min="1"
            step="5"
            :value="durationMinutes"
            :class="[
              fieldClass,
              {
                '!border-red-500': submitted && fieldErrors.duration,
              },
            ]"
            required
            @change="handleDurationChange"
          />
          <p
            v-if="submitted && fieldErrors.duration"
            class="mt-1 mb-0 text-xs text-red-500"
          >
            {{ $t('REAL_ESTATE.FORM.REQUIRED') }}
          </p>
        </div>

        <div>
          <p class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.APPOINTMENTS.FIELD_AVAILABILITY') }}
            <span class="text-red-500">*</span>
          </p>
          <AvailabilityPicker
            :property-id="appointment.property.property_id"
            :appointment-type="appointment.appointment_type"
            :booking-mode="bookingMode"
            :duration-minutes="isKeyHandover ? durationMinutes : null"
            :agents="project.agents"
            @change="handleAvailabilityChange"
          />
          <p
            v-if="submitted && fieldErrors.availability"
            class="mt-1 mb-0 text-xs text-red-500"
          >
            {{ $t('REAL_ESTATE.FORM.REQUIRED') }}
          </p>
        </div>

        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.APPOINTMENTS.FIELD_RESCHEDULE_REASON') }}
          </label>
          <input
            v-model="reason"
            :placeholder="
              $t('REAL_ESTATE.APPOINTMENTS.FIELD_RESCHEDULE_REASON_PLACEHOLDER')
            "
            type="text"
            :class="fieldClass"
          />
        </div>

        <div
          class="flex justify-end gap-3 pt-4 mt-2 border-t border-slate-200 dark:border-slate-700"
        >
          <woot-button variant="clear" type="button" @click="$emit('close')">
            {{ $t('REAL_ESTATE.APPOINTMENTS.CANCEL') }}
          </woot-button>
          <woot-button type="submit" :is-loading="isSubmitting">
            {{ $t('REAL_ESTATE.APPOINTMENTS.SAVE') }}
          </woot-button>
        </div>
      </form>
    </div>
  </div>
</template>
